import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/auth.service';
import { StorageService } from '@/services/storage.service';
import { User, AuthResponse } from '@/types/auth';

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  
  // Actions
  initialize: () => Promise<void>;
  signInWithMagicLink: (email: string, redirectTo?: string) => Promise<void>;
  signInWithGoogle: (redirectTo?: string) => Promise<void>;
  verifyMagicLink: (email: string, token: string, type: string) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  updateUser: (user: User) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Inicialização síncrona com dados do localStorage (já persistidos)
      let initialUser: User | null = null;
      const initialLoading = false;
      
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          initialUser = currentUser.user;
        }
      } catch (error) {
        // Ignora erros na inicialização
      }

      return {
        user: initialUser,
        loading: initialLoading,
        initialized: false,

        initialize: async () => {
          if (get().initialized) return;
          
          const hasUser = !!get().user;
          if (!hasUser) {
            set({ loading: true });
          }
          
          try {
            const currentUser = authService.getCurrentUser();
            if (currentUser) {
              set({ user: currentUser.user, initialized: true, loading: false });
            } else {
              // Se só o accessToken expirou/perdeu, tenta restaurar sessão com refreshToken
              try {
                const restored = await authService.restoreSession();
                if (restored?.user) {
                  set({ user: restored.user, initialized: true, loading: false });
                  return;
                }
              } catch (restoreError) {
                // sessão inválida: segue como deslogado
              }
              set({ user: null, initialized: true, loading: false });
            }
          } catch (error) {
            console.error('Error initializing auth:', error);
            set({ initialized: true, loading: false });
          }
        },

        signInWithMagicLink: async (email: string, redirectTo?: string) => {
          await authService.requestMagicLink(email, redirectTo);
        },

        signInWithGoogle: async (redirectTo?: string) => {
          await authService.signInWithGoogle(redirectTo);
        },

        verifyMagicLink: async (email: string, token: string, type: string) => {
          const authResponse = await authService.verifyMagicLink(email, token, type);
          set({ user: authResponse.user });
          return authResponse;
        },

        signOut: async () => {
          await authService.signOut();
          set({ user: null });
        },

        updateUser: (updatedUser: User) => {
          set({ user: updatedUser });
          StorageService.setUser(updatedUser);
        },

        setLoading: (loading: boolean) => {
          set({ loading });
        },
      };
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
      onRehydrateStorage: () => (state) => {
        if (!state?.user) return;
        const hasSession =
          !!StorageService.getRefreshToken() ||
          (!!StorageService.getAccessToken() && !!StorageService.getUser());
        if (!hasSession) {
          state.user = null;
        }
      },
    }
  )
);
